import { CombinedError } from '@urql/core';

import { findGraphqlErrorWithCode } from '~/utils/graphql';

interface UnifiedErrorOptions {
  logMessage: string;
  context?: Record<string, unknown>;
  toast?: string | false;
}

export const useUnifiedErrorHandler = () => {
  const { $logErrorMessage } = useNuxtApp();
  const { show: showToast } = useToast();
  const { t } = useI18n();

  // A deterministic AI decline (platform content filter or model refusal): don't offer retry —
  // the identical input re-bills the identical decline. Legacy CONTENT_FILTERED is kept for
  // deploy-window skew against an older backend.
  const isContentDeclinedError = (err: unknown): boolean => {
    return err instanceof CombinedError
      && (!!findGraphqlErrorWithCode(err, 'CONTENT_DECLINED') || !!findGraphqlErrorWithCode(err, 'CONTENT_FILTERED'));
  };

  // Someone answered this question already (another tab, a double tap). The server holds the
  // answer, so a re-sync settles the question correctly — a retry would only repeat the conflict.
  const isQuestionAlreadyAnsweredError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'QUESTION_ALREADY_ANSWERED');
  };

  // The question itself is no longer the one the session is holding — spoken past, superseded, or
  // never there. The options are dead where they stand, and the client re-reads the session so its
  // own copy of them closes: a session ended by an idle sweep publishes nothing, so a tab that only
  // re-enabled the question would refuse every tap forever.
  const isQuestionNotFoundError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'QUESTION_NOT_FOUND');
  };

  // The picks do not answer the question: an option id it never carried, or the wrong number of them
  // for its select type. A client sending what it was given never sees this; re-sending cannot fix it.
  const isUnusableQuestionAnswerError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'UNUSABLE_QUESTION_ANSWER');
  };

  // The session the mutation targeted is no longer active. Never a failure to report: the send
  // path opens one and resends, and the end path is already at the outcome it asked for.
  const isNoActiveSessionError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'NO_ACTIVE_SESSION');
  };

  // The session refuses to be switched again: another switch already took it, so the card offering
  // this one is answering for a decision that has been made. Nothing to retry — the offer is over.
  const isSessionSwitchLockedError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'SESSION_SWITCH_LOCKED');
  };

  // The session has nothing in it yet, so there is nothing to end — the server declining to close a
  // session that has not started is the contract working, not a failure to report.
  const isSessionHasNoEventsError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'SESSION_HAS_NO_EVENTS');
  };

  // An activity event the server would not file under the navigation it named, because that visit
  // is gone. A page whose own navigation had not echoed yet files its event under the one before, so
  // this is the bookkeeping write missing, not the student doing anything wrong.
  const isActivityNavigationNotFoundError = (err: unknown): boolean => {
    return err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'ACTIVITY_NAVIGATION_NOT_FOUND');
  };

  const handleError = (err: unknown, options: UnifiedErrorOptions) => {
    const isUnauthenticated = err instanceof CombinedError && !!findGraphqlErrorWithCode(err, 'UNAUTHENTICATED');
    if (isUnauthenticated) {
      return;
    }

    $logErrorMessage(options.logMessage, { error: err, ...options.context });

    if (options.toast !== false) {
      showToast({
        variant: 'error',
        message: options.toast ?? t('unified.errors.genericFailed'),
      });
    }
  };

  return { handleError, isContentDeclinedError, isActivityNavigationNotFoundError, isNoActiveSessionError, isQuestionAlreadyAnsweredError, isQuestionNotFoundError, isSessionHasNoEventsError, isSessionSwitchLockedError, isUnusableQuestionAnswerError };
};
